import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { checkSupportedExchange } from '@helpers/utils/asset-price/asset-price';
import { PriceStatus } from '@helpers/utils/numbers/numbers';
import { useIsAwaitingLivePrice } from '@terminal/hooks/use-live-price';
import { usePriceDisplay } from '@terminal/react-query-hooks/prices/usePriceDisplay';
import clsx from 'clsx';

import styles from './InsightsLivePriceChange.module.scss';
import { getPriceChange } from './utils';

const priceStatusClassMap: Record<PriceStatus, string> = {
  [PriceStatus.Default]: semanticColorStyles.directionNeutralSoft,
  [PriceStatus.Negative]: semanticColorStyles.directionNegative,
  [PriceStatus.Positive]: semanticColorStyles.directionPositive,
};

export interface InsightsLivePriceChangeProps {
  className?: string;
  entity: Entity;
  isWindowComplete?: boolean;
  waitForLiveTick?: boolean;
}

export const InsightsLivePriceChange = ({
  className,
  entity,
  isWindowComplete,
  waitForLiveTick = false,
}: InsightsLivePriceChangeProps) => {
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);
  const hasLiveTick = !!(
    priceData?.price.primary.isLive || priceData?.price.secondary?.isLive
  );
  const canReceiveLiveTick =
    !!entity.subscribable_ticker &&
    checkSupportedExchange((entity.exchange?.code ?? '').toLowerCase());
  const isAwaitingLiveTick = useIsAwaitingLivePrice({
    hasLiveTick,
    marketHours: priceData?.price.primary.marketHours,
    enabled: waitForLiveTick && canReceiveLiveTick,
  });

  if (isLoading || isAwaitingLiveTick) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  const priceChange =
    isValid && priceData
      ? getPriceChange(priceData.price, isWindowComplete)
      : undefined;

  if (!priceChange) {
    return null;
  }

  return (
    <div data-testid="insight-live-price-change">
      <div
        className={clsx(
          styles.percentageChangeLabel,
          priceStatusClassMap[priceChange.status],
          className
        )}
      >
        {priceChange.proportionChange}
      </div>
    </div>
  );
};
